export type AudioUplinkTelemetryLog = (
  event: string,
  detail?: Record<string, string | number | boolean | null>,
  durMs?: number | null,
) => void;

/**
 * Why a stream stopped.
 *
 * ``recording_stopped`` is the ordinary end now that the engine owns the stream: it
 * lasts as long as the recording does. ``unmount`` is kept because it is what the
 * old component-owned lifecycle reported, and a stop reason is a telemetry value
 * that outlives the code that wrote it -- a series still holds these rows.
 */
export type AudioUplinkStopReason =
  | "explicit"
  | "closed"
  | "error"
  | "readiness_timeout"
  | "recording_stopped"
  | "unmount"
  | "reconnect"
  | "reconnect_failed";

type AudioUplinkVisibility = "visible" | "hidden" | "prerender" | "unloaded" | "unknown";

/**
 * Which reconnect lane an attempt belongs to (#264 S2).
 *
 * "abnormal" is the original bounded recovery: 2 attempts, 1 s then 4 s, for a drop
 * nobody announced. "restart" is the deploy lane: 3 attempts over 3 minutes, opened by a
 * 1012/1013 close. They are separate series and not comparable, which is exactly why the
 * lane is on the wire -- a `gap_ms` of 30 s is a healthy restart attempt and a
 * catastrophic abnormal one, and a reader cannot tell which without this field.
 *
 * "slow" (#356) is what follows either lane once its budget is spent, or a start that
 * failed or timed out: one attempt every `STREAM_SLOW_RETRY_MS` for as long as the
 * recording lasts. It used to be the point where the lane failed closed.
 *
 * "resumable" (#410) follows a 1008 the gateway keeps the cursor for -- the
 * per-connection cap, most often. Its own backoff, reset by progress, and no budget.
 */
export type StreamProbeReconnectLane = "abnormal" | "restart" | "slow" | "resumable";

/**
 * The `detail` payload of the `stream_probe_reconnect` telemetry event.
 *
 * Keeps the `StreamProbe` spelling on purpose (#330): this type is named after a
 * frozen wire string, and the event name cannot change without orphaning every row
 * already in the series. Renaming the type while the event stayed put would create a
 * *new* name/contract mismatch -- the exact defect #330 removes. Everything on the
 * transport that is not named after a wire string is `AudioUplink*`.
 */
export interface StreamProbeReconnectDetail {
  close_code: number | null;
  close_reason: string | null;
  timeline_epoch: string | null;
  gap_ms: number;
  /**
   * Counted within `lane`, not across both. An "attempt 1" on the restart lane can
   * follow an "attempt 2" on the abnormal one; the pair (lane, attempt) is what
   * identifies a reconnect, and the uplink's own `attempt` in `stream_start`/`stream_stop`
   * stays globally monotonic for anyone who needs the total.
   */
  lane: StreamProbeReconnectLane;
  [key: string]: string | number | boolean | null | undefined;
}

function pageVisibility(): AudioUplinkVisibility {
  if (typeof document === "undefined") return "unknown";
  const visibilityState = document.visibilityState as string;
  switch (visibilityState) {
    case "visible":
    case "hidden":
    case "prerender":
      return visibilityState;
    default:
      return "unknown";
  }
}

/**
 * Own the client-side lifecycle timestamps for one audio uplink.
 *
 * An uplink can stop through explicit UI, socket close, an error, or the recording
 * ending. Keeping the pairing here makes all of those paths idempotent: exactly one
 * successful start can produce exactly one stop, and a failed start produces neither
 * half of the pair.
 */
export class AudioUplinkTelemetry {
  private readonly log: AudioUplinkTelemetryLog;
  private startedAtMs: number | null = null;
  private firstTurnReported = false;
  private freezeResumeObserved = false;
  private bufferedFrameHighWater = 0;
  private readonly observeFreezeResume = () => {
    if (this.startedAtMs !== null) this.freezeResumeObserved = true;
  };

  constructor(log: AudioUplinkTelemetryLog) {
    this.log = log;
    if (typeof document !== "undefined") {
      document.addEventListener("freeze", this.observeFreezeResume);
      document.addEventListener("resume", this.observeFreezeResume);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("freeze", this.observeFreezeResume);
      window.addEventListener("resume", this.observeFreezeResume);
    }
  }

  start(atMs: number, mode: "auto" | "manual" = "auto", attempt = 1): boolean {
    if (this.startedAtMs !== null) return false;
    this.startedAtMs = atMs;
    this.firstTurnReported = false;
    this.freezeResumeObserved = false;
    this.bufferedFrameHighWater = 0;
    this.log("stream_start", { mode, attempt });
    return true;
  }

  firstTurn(turnOrder: number, atMs: number): boolean {
    if (this.startedAtMs === null || this.firstTurnReported) return false;
    this.firstTurnReported = true;
    this.log("stream_first_turn", { turn_order: turnOrder }, atMs - this.startedAtMs);
    return true;
  }

  reconnect(
    _atMs: number,
    attempt: number,
    detail: StreamProbeReconnectDetail,
  ): boolean {
    this.log("stream_probe_reconnect", {
      attempt,
      close_code: detail.close_code,
      close_reason: detail.close_reason,
      timeline_epoch: detail.timeline_epoch,
      gap_ms: detail.gap_ms,
      lane: detail.lane,
      visibility: pageVisibility(),
      freeze_resume_observed: this.freezeResumeObserved,
      buffered_frame_high_water: this.bufferedFrameHighWater,
    });
    return true;
  }

  stop(
    atMs: number,
    reason: AudioUplinkStopReason,
    detail: Record<string, string | number | boolean | null> = {},
    mode: "auto" | "manual" = "auto",
    attempt = 1,
  ): boolean {
    if (this.startedAtMs === null) return false;
    this.log(
      "stream_stop",
      {
        ...detail,
        reason,
        mode,
        attempt,
        visibility: pageVisibility(),
        freeze_resume_observed: this.freezeResumeObserved,
        buffered_frame_high_water: this.bufferedFrameHighWater,
      },
      atMs - this.startedAtMs,
    );
    this.startedAtMs = null;
    this.firstTurnReported = false;
    return true;
  }

  /** Record the greatest number of complete audio frames waiting behind the sender. */
  noteBufferedFrameHighWater(frames: number): void {
    if (!Number.isFinite(frames)) return;
    this.bufferedFrameHighWater = Math.max(
      this.bufferedFrameHighWater,
      Math.max(0, Math.floor(frames)),
    );
  }

  reset(): void {
    this.startedAtMs = null;
    this.firstTurnReported = false;
    this.freezeResumeObserved = false;
    this.bufferedFrameHighWater = 0;
  }
}
